import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ShoppingCart, Check, Star, ArrowLeft, Truck, Shield, Award, RefreshCw,
  Heart, GitCompare, Share2, ChevronRight, Play, X, MessageSquare, BadgeCheck, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import { sanitizeHTML } from '../utils/sanitize';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PincodeChecker from '../components/PincodeChecker';
import { toast } from 'sonner';
import { trackEvent } from '../components/TrackingPixels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get or create anonymous user ID for tracking
const getAnonymousUserId = () => {
  let anonId = localStorage.getItem('anon_user_id');
  if (!anonId) {
    anonId = 'anon_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('anon_user_id', anonId);
  }
  return anonId;
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [pincodeAvailable, setPincodeAvailable] = useState(null);
  const [inWishlist, setInWishlist] = useState(false);
  const [inCompare, setInCompare] = useState(false);
  const [showVideo, setShowVideo] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', review: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const userId = user?.user_id || getAnonymousUserId();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/products/${id}`);
        if (res.ok) {
          const data = await res.json();
          setProduct(data);
          
          // Track recently viewed
          fetch(`${BACKEND_URL}/api/recently-viewed?user_id=${userId}&product_id=${id}`, { 
            method: 'POST'
          }).catch(() => {});
          
          // Fetch related products
          const relatedRes = await fetch(`${BACKEND_URL}/api/products/${id}/related?limit=4`);
          if (relatedRes.ok) {
            setRelatedProducts(await relatedRes.json());
          }
          
          // Fetch recently viewed products
          const recentRes = await fetch(`${BACKEND_URL}/api/recently-viewed/${userId}?limit=6`);
          if (recentRes.ok) {
            const recentData = await recentRes.json();
            // Filter out current product
            setRecentlyViewed(recentData.filter(p => p.id !== id));
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    fetchReviews();
    window.scrollTo(0, 0);
  }, [id]);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${id}/reviews?limit=50`);
      if (res.ok) setReviews(await res.json());
    } catch { /* silent */ }
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit a review');
      navigate('/login', { state: { from: `/product/${id}` } });
      return;
    }
    if (!reviewForm.review.trim()) {
      toast.error('Please write a review');
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${id}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: id,
          rating: reviewForm.rating,
          title: reviewForm.title || null,
          review: reviewForm.review,
          images: []
        })
      });
      if (res.ok) {
        toast.success('Review submitted! It will appear after admin approval.');
        setReviewForm({ rating: 5, title: '', review: '' });
        setShowReviewForm(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not submit review');
      }
    } catch {
      toast.error('Failed to submit review');
    }
    setSubmittingReview(false);
  };

  const handleAddToCart = () => {
    if (!pincodeAvailable) {
      toast.error('Please check delivery availability for your pincode first');
      return;
    }
    if (!pincodeAvailable.available) {
      toast.error('Delivery not available in your area');
      return;
    }
    
    addToCart({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      discount_price: product.discount_price,
      image: product.images?.[0] || product.image,
      images: product.images
    }, quantity);
    toast.success('Added to cart!');
  };

  const handleBuyNow = () => {
    if (!pincodeAvailable?.available) {
      toast.error('Please check delivery availability first');
      return;
    }
    
    addToCart({
      id: product.id,
      name: product.name,
      brand: product.brand,
      price: product.price,
      discount_price: product.discount_price,
      image: product.images?.[0] || product.image,
      images: product.images
    }, quantity);
    navigate('/checkout');
  };

  const handleAddToWishlist = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/product/${id}` } });
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/user/wishlist/${id}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setInWishlist(true);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      toast.error('Failed to add to wishlist');
    }
  };

  const handleAddToCompare = async () => {
    const sessionId = localStorage.getItem('comparison_session') || `sess_${Date.now()}`;
    localStorage.setItem('comparison_session', sessionId);

    try {
      const res = await fetch(`${BACKEND_URL}/api/compare/add/${id}?session_id=${sessionId}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setInCompare(true);
        toast.success('Added to compare!', {
          action: {
            label: 'Compare',
            onClick: () => navigate('/compare')
          }
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add to compare');
      }
    } catch (error) {
      toast.error('Failed to add to comparison');
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: product.name,
        text: `Check out ${product.name} at RO Dekho!`,
        url: window.location.href
      });
    } catch {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Product not found</h2>
            <Button onClick={() => navigate('/products')}>Back to Products</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Get all images
  const allImages = [product.image, ...(product.images || [])].filter(Boolean);
  
  // Get videos from media
  const videos = product.media?.filter(m => m.type === 'video') || [];

  // ----- SEO computed values -----
  const siteUrl = 'https://www.rodekho.in';
  const canonical = `${siteUrl}/product/${product.slug || product.id}`;
  const metaTitle = product.meta_title || `${product.name} - Buy Online at Best Price | RO Dekho`;
  const fallbackDesc = `Buy ${product.name} ${product.brand ? `from ${product.brand}` : ''} at the best price in India. ${product.description ? product.description.slice(0, 110) : ''} Free installation, COD available, 1 year warranty.`;
  const metaDescription = (product.meta_description || fallbackDesc).slice(0, 300);
  const ogImage = product.og_image || allImages[0] || '';
  const keywords = Array.isArray(product.meta_keywords) ? product.meta_keywords.join(', ') : (product.meta_keywords || '');

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: metaDescription,
    image: allImages.length ? allImages : undefined,
    sku: product.sku || product.id,
    brand: product.brand ? { '@type': 'Brand', name: product.brand } : undefined,
    category: product.category_name || product.category || undefined,
    offers: {
      '@type': 'Offer',
      url: canonical,
      priceCurrency: 'INR',
      price: product.price,
      availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'RO Dekho' }
    },
    ...(product.reviews > 0 && product.rating > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviews
      }
    } : {}),
    ...(reviews.length > 0 ? {
      review: reviews.slice(0, 5).map(r => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.user_name },
        datePublished: r.created_at,
        reviewBody: r.review,
        name: r.title || undefined,
        reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 }
      }))
    } : {})
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {keywords && <meta name="keywords" content={keywords} />}
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:locale" content="en_IN" />
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content="INR" />
        <meta property="product:availability" content={product.inStock ? 'in stock' : 'out of stock'} />
        {product.brand && <meta property="product:brand" content={product.brand} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-blue-600">Home</Link>
          <ChevronRight size={16} />
          <Link to="/products" className="hover:text-blue-600">Products</Link>
          <ChevronRight size={16} />
          <span className="text-gray-900">{product.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
          {/* Product Images */}
          <div>
            <div className="mb-4 rounded-xl overflow-hidden border-2 border-gray-100 relative group">
              <img
                src={allImages[selectedImage]}
                alt={product.name}
                className="w-full h-96 object-contain bg-white"
              />
              {product.discount > 0 && (
                <Badge className="absolute top-4 left-4 bg-red-500 text-white text-lg px-3 py-1">
                  {product.discount}% OFF
                </Badge>
              )}
            </div>
            
            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-blue-700 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-contain bg-white" />
                  </button>
                ))}
                {/* Video thumbnails */}
                {videos.map((video, index) => (
                  <button
                    key={`video-${index}`}
                    onClick={() => setShowVideo(video.url)}
                    className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-300 relative bg-gray-100"
                  >
                    <Play className="absolute inset-0 m-auto w-8 h-8 text-blue-600" />
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-xs bg-black/70 text-white px-1 rounded">Video</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">{product.brand}</Badge>
              {product.technology && (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-200">{product.technology}</Badge>
              )}
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
            
            {/* Rating */}
            <a href="#reviews" className="flex items-center gap-2 mb-4 group" data-testid="product-rating-anchor">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    className={i < Math.floor(product.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600 group-hover:text-blue-700 group-hover:underline">
                {product.rating || 0} ({product.reviews || 0} review{(product.reviews || 0) === 1 ? '' : 's'})
              </span>
            </a>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl sm:text-4xl font-bold text-blue-700">₹{product.price?.toLocaleString()}</span>
                {product.originalPrice > product.price && (
                  <>
                    <span className="text-xl text-gray-400 line-through">₹{product.originalPrice?.toLocaleString()}</span>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-200">{product.discount}% OFF</Badge>
                  </>
                )}
              </div>
              <p className="text-sm text-gray-600">Inclusive of all taxes</p>
            </div>

            {/* Stock Status */}
            <div className={`flex items-center gap-2 mb-6 ${product.inStock ? 'text-green-600' : 'text-red-600'}`}>
              {product.inStock ? (
                <>
                  <Check size={20} />
                  <span className="font-semibold">In Stock - Ready to Ship</span>
                </>
              ) : (
                <>
                  <X size={20} />
                  <span className="font-semibold">Out of Stock</span>
                </>
              )}
            </div>

            {/* Short Description */}
            {product.description && (
              <p className="text-gray-700 mb-6 leading-relaxed">{product.description}</p>
            )}

            {/* Pincode Checker */}
            <PincodeChecker 
              className="mb-6"
              onAvailable={(data) => setPincodeAvailable(data)}
              onUnavailable={(data) => setPincodeAvailable(data)}
            />

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-blue-700 transition-colors"
                >
                  -
                </button>
                <span className="px-6 py-2 bg-gray-100 rounded-lg font-semibold">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:border-blue-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-3 sm:gap-4 mb-6">
              <Button
                onClick={handleAddToCart}
                variant="outline"
                disabled={!product.inStock}
                className="flex-1 border-2 border-blue-700 text-blue-700 hover:bg-blue-50 py-4 sm:py-6 text-sm sm:text-lg font-semibold disabled:opacity-50"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart size={18} className="mr-1 sm:mr-2" />
                Add to Cart
              </Button>
              <Button
                onClick={handleBuyNow}
                disabled={!product.inStock}
                className="flex-1 bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white py-4 sm:py-6 text-sm sm:text-lg font-semibold shadow-lg hover:shadow-xl disabled:opacity-50"
                data-testid="buy-now-btn"
              >
                Buy Now
              </Button>
            </div>

            {/* Secondary Actions */}
            <div className="flex flex-wrap gap-2 sm:gap-4 mb-6">
              <button
                onClick={handleAddToWishlist}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  inWishlist ? 'bg-red-50 border-red-300 text-red-600' : 'border-gray-300 hover:border-red-300 hover:text-red-600'
                }`}
              >
                <Heart size={18} className={inWishlist ? 'fill-current' : ''} />
                {inWishlist ? 'In Wishlist' : 'Wishlist'}
              </button>
              <button
                onClick={handleAddToCompare}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  inCompare ? 'bg-blue-50 border-blue-300 text-blue-600' : 'border-gray-300 hover:border-blue-300 hover:text-blue-600'
                }`}
              >
                <GitCompare size={18} />
                {inCompare ? 'In Compare' : 'Compare'}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors"
              >
                <Share2 size={18} />
                Share
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Truck className="text-blue-700 shrink-0" size={18} />
                <span className="text-xs sm:text-sm font-medium">Free Installation</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="text-blue-700 shrink-0" size={18} />
                <span className="text-xs sm:text-sm font-medium">{product.specifications?.warranty || '1 Year Warranty'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="text-blue-700 shrink-0" size={18} />
                <span className="text-xs sm:text-sm font-medium">Cash on Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="text-blue-700 shrink-0" size={18} />
                <span className="text-xs sm:text-sm font-medium">7 Days Return</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rich Description */}
        {product.rich_description && (
          <div className="mt-8 bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Description</h2>
            <div 
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.rich_description) }}
            />
          </div>
        )}

        {/* Specifications */}
        {product.specifications && Object.keys(product.specifications).length > 0 && (
          <div className="mt-8 bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Specifications</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(product.specifications).filter(([_, v]) => v).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-700 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className="text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        {product.features && product.features.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
            <ul className="grid md:grid-cols-2 gap-4">
              {product.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                  <Check className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Reviews & Ratings */}
        <div id="reviews" className="mt-8 bg-white rounded-2xl p-6 sm:p-8 shadow-lg" data-testid="reviews-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <MessageSquare size={22} className="text-blue-600" /> Customer Reviews
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20}
                      className={i < Math.round(product.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                  ))}
                </div>
                <span className="font-semibold text-gray-900">{(product.rating || 0).toFixed(1)}</span>
                <span className="text-gray-500 text-sm">based on {product.reviews || 0} review{(product.reviews || 0) === 1 ? '' : 's'}</span>
              </div>
            </div>
            <Button onClick={() => {
              if (!isAuthenticated) { navigate('/login', { state: { from: `/product/${id}` } }); return; }
              setShowReviewForm(v => !v);
            }} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="write-review-btn">
              {showReviewForm ? 'Cancel' : 'Write a Review'}
            </Button>
          </div>

          {showReviewForm && isAuthenticated && (
            <form onSubmit={submitReview} className="mb-8 p-5 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-100 rounded-xl space-y-4" data-testid="review-form">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Your Rating</label>
                <div className="flex gap-1" data-testid="review-rating-stars">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setReviewForm(f => ({ ...f, rating: n }))}
                      className="p-1 hover:scale-110 transition-transform" data-testid={`review-star-${n}`}>
                      <Star size={28} className={n <= reviewForm.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                    </button>
                  ))}
                  <span className="ml-2 self-center text-sm text-gray-500">{reviewForm.rating}/5</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Title (optional)</label>
                <input type="text" maxLength={120} value={reviewForm.title}
                  onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Excellent product!"
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  data-testid="review-title-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Your Review *</label>
                <textarea required rows={4} value={reviewForm.review}
                  onChange={e => setReviewForm(f => ({ ...f, review: e.target.value }))}
                  placeholder="Share your experience with this product..."
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-y"
                  data-testid="review-text-input" />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Your review will be visible after admin approval.</p>
                <Button type="submit" disabled={submittingReview}
                  className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="submit-review-btn">
                  {submittingReview ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                  Submit Review
                </Button>
              </div>
            </form>
          )}

          {showReviewForm && !isAuthenticated && (
            <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Please <Link to="/login" className="font-semibold underline">log in</Link> to submit a review.
            </div>
          )}

          {reviews.length === 0 ? (
            <div className="text-center py-10 text-gray-500" data-testid="reviews-empty">
              <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="font-medium">No reviews yet</p>
              <p className="text-sm">Be the first to share your experience with this product!</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="reviews-list">
              {reviews.map((r) => (
                <div key={r.review_id} className="border-2 border-gray-100 rounded-xl p-4 hover:border-blue-100 transition-colors" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{r.user_name}</span>
                        {r.is_verified_purchase && (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                            <BadgeCheck size={12} /> Verified Purchase
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={14} className={i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
                          ))}
                        </div>
                        {r.created_at && (
                          <span className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {r.title && <h4 className="font-semibold text-gray-900 mb-1">{r.title}</h4>}
                  <p className="text-gray-700 text-sm leading-relaxed">{r.review}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Products</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relProduct) => (
                <Link
                  key={relProduct.id}
                  to={`/product/${relProduct.id}`}
                  className="group bg-gray-50 rounded-xl p-4 hover:shadow-lg transition-shadow"
                >
                  <img
                    src={relProduct.image}
                    alt={relProduct.name}
                    className="w-full h-40 object-contain mb-4 group-hover:scale-105 transition-transform"
                  />
                  <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600">
                    {relProduct.name}
                  </h3>
                  <p className="text-lg font-bold text-blue-600">₹{relProduct.price?.toLocaleString()}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recently Viewed Products */}
      {recentlyViewed.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recently Viewed</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {recentlyViewed.slice(0, 6).map((item) => (
              <Link
                key={item.id}
                to={`/product/${item.id}`}
                className="bg-white rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow group"
              >
                <div className="aspect-square mb-2 bg-gray-50 rounded-lg overflow-hidden">
                  <img
                    src={item.images?.[0] || item.image || '/placeholder.jpg'}
                    alt={item.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                  />
                </div>
                <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">{item.name}</h3>
                <p className="text-sm font-semibold text-blue-600">
                  ₹{(item.discount_price || item.price)?.toLocaleString()}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setShowVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X size={32} />
            </button>
            <div className="relative pb-[56.25%] h-0">
              <iframe
                src={showVideo}
                className="absolute top-0 left-0 w-full h-full rounded-xl"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
